import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** The candidate's fallback stays on the dark interview scene they were already looking at. */
  variant?: 'app' | 'interview';
}

interface State {
  error?: Error;
}

/**
 * Catches an error thrown while rendering and shows a way out.
 *
 * Without one, React unmounts the whole tree on an uncaught render error and
 * the page goes blank, with the only trace in the console - which is what
 * happened to every candidate when the interview page once rendered without its
 * ConversationProvider.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    if (this.props.variant === 'interview') {
      return (
        <div className="interview-scene">
          <main className="interview-content">
            <div className="interview-intro" role="alert">
              <h2>Something went wrong on this page.</h2>
              <p className="muted">
                If you had not pressed Start yet, your link still works - reload to try again. If your
                interview had already begun, contact your recruiter for a new link.
              </p>
              <button className="scene-button primary" onClick={() => window.location.reload()}>
                Reload
              </button>
            </div>
          </main>
        </div>
      );
    }

    return (
      <main className="shell-main">
        <div className="card stack" role="alert">
          <h1>Something went wrong</h1>
          <p className="muted">
            This page hit an unexpected error. Reloading usually fixes it, and anything you had already
            saved is safe.
          </p>
          <div className="row">
            <button onClick={() => window.location.reload()}>Reload</button>
            <a href="/">Back to roles</a>
          </div>
        </div>
      </main>
    );
  }
}
